/** Fetches a session token via App Bridge v4 (`shopify.idToken()`), flagging auth failures. */

import { useAuthStore } from '@bundles/store/authStore'
import {
  getAdminHomeUrl,
  isShopifyAuthFailure,
  normalizeReauthStatus,
  resolveAuthFailureInstallUrl,
} from '@bundles/utils/shopifyAuthFailure'

/** Returns the App Bridge v4 global injected by the CDN script, or null outside the admin. */
export const getShopifyGlobal = (): typeof shopify | null => {
  if (typeof window === 'undefined' || typeof shopify === 'undefined') {
    return null
  }
  return shopify
}

/** Replaces `getSessionToken(app)` from App Bridge v3 — uses `shopify.idToken()`. */
export const getSessionToken = async (): Promise<string> => {
  const shopify = getShopifyGlobal()
  if (!shopify?.idToken) {
    throw new Error('App Bridge not initialized - shopify global not found')
  }

  const token = await shopify.idToken()
  if (!token) {
    throw new Error('Failed to retrieve a session token')
  }

  return token
}

export const handleShopifyAuthFailure = (
  error: unknown,
  reauthStatus?: string | null,
) => {
  if (!isShopifyAuthFailure(error)) {
    return false
  }

  const store = useAuthStore.getState()
  if (store.authFailure) {
    return true
  }

  // Session expired → admin.shopify.com/apps/{API_KEY}; giữ shop/host trong store.
  const status = normalizeReauthStatus(reauthStatus) ?? 'session_expired'
  const installUrl = resolveAuthFailureInstallUrl(status)
  const adminHomeUrl = getAdminHomeUrl()

  useAuthStore.setState(
    {
      token: null,
      isLoading: false,
      authFailure: {
        installUrl,
        adminHomeUrl,
        reauthStatus: status,
      },
    },
    false,
  )
  localStorage.removeItem('whoami')

  return true
}
export const getSessionTokenOrRedirect = async () => {
  try {
    return await getSessionToken()
  } catch (error) {
    handleShopifyAuthFailure(error)
    throw error
  }
}

export type ToastOptions = { duration?: number; isError?: boolean }
export type ToastPayload = ToastOptions & { message: string }

/** Replaces v3 `Toast.create(app, …).dispatch(Toast.Action.SHOW)`. */
export const showToast = (
  messageOrPayload: string | ToastPayload,
  options?: ToastOptions,
): string | null => {
  if (typeof messageOrPayload === 'string') {
    return getShopifyGlobal()?.toast.show(messageOrPayload, options) ?? null
  }

  const { message, ...toastOptions } = messageOrPayload
  return getShopifyGlobal()?.toast.show(message, toastOptions) ?? null
}

/** Replaces v3 `toast.dispatch(Toast.Action.CLEAR)`. */
export const hideToast = (toastId: string | null | undefined): void => {
  if (!toastId) {
    return
  }

  getShopifyGlobal()?.toast.hide(toastId)
}

/** Replaces v3 `Loading.create(app).dispatch(Loading.Action.START/STOP)`. */
export const setAppLoading = (isLoading: boolean): void => {
  getShopifyGlobal()?.loading(isLoading)
}
